import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { fetchUserProfile } from "@/hooks/useUser";

export interface SettingsProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface UpdateProfileDTO {
  firstName: string;
  lastName: string;
}

export interface UpdatePasswordDTO {
  password: string;
}

class SettingsService {
  private static instance: SettingsService;

  private constructor() {}

  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }

  async getProfile(): Promise<SettingsProfile> {
    const { user, profile } = await queryClient.fetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: 1000 * 60 * 5,
    });

    return {
      id: user.id,
      email: user.email ?? "",
      firstName: profile?.first_name || "",
      lastName: profile?.last_name || "",
    };
  }

  async updateProfile(userId: string, data: UpdateProfileDTO) {
    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: data.firstName,
        last_name: data.lastName,
      })
      .eq("id", userId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ["authUser"] });

    return true;
  }

  /**
   * ponytail: the current password is checked client-side with
   * signInWithPassword. The real guarantee is Supabase's "Secure password
   * change" setting — without it updateUser can still be called directly with
   * a valid session, bypassing this form.
   */
  async updatePassword(currentPassword: string, newPassword: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user?.email) throw new Error("Not signed in");

    const { error: wrongPassword } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });

    if (wrongPassword) throw new Error("Current password is incorrect");

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
    return true;
  }
}

export const settingsService = SettingsService.getInstance();
